import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import QRCode from "qrcode";
import { useEffect, useRef, useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import { Logo } from "../componentes/Barras.tsx";
import {
  BarrasLatiendo,
  Confeti,
  FondoDeReto,
  LetraOpcion,
  PasosDelReto,
  ReaccionesFlotantes,
  Reloj,
} from "../componentes/Reto.tsx";
import { Aviso, Boton, EstadoVacio } from "../componentes/ui.tsx";
import type { VistaDePresentador } from "../tipos.ts";
import { mensajeDe } from "../utiles.ts";

const INTERVALO_MS = 1000;

/** Lo que se proyecta en la sala, y desde donde quien expone lleva el ritmo. */
export function PaginaPresentar() {
  const { id } = useParams({ from: "/movil/partidas/$id/presentar" });
  const trpc = useTRPC();
  const [error, setError] = useState<string | null>(null);
  const vista = useQuery({
    ...trpc.quices.presentador.queryOptions({ id }),
    staleTime: 0,
    // Sigue consultando en el podio: la sala todavía manda sus reacciones.
    refetchInterval: INTERVALO_MS,
  });
  const alTerminar = {
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries();
    },
    onError: (e: unknown) => setError(mensajeDe(e)),
  };
  const iniciar = useMutation(trpc.quices.iniciar.mutationOptions(alTerminar));
  const siguiente = useMutation(trpc.quices.siguiente.mutationOptions(alTerminar));
  const terminar = useMutation(trpc.quices.terminar.mutationOptions(alTerminar));
  const ocupado = iniciar.isPending || siguiente.isPending || terminar.isPending;

  if (vista.error) return <EstadoVacio>{mensajeDe(vista.error)}</EstadoVacio>;
  if (!vista.data) return <EstadoVacio>Cargando…</EstadoVacio>;
  const v = vista.data;
  const enEspera = v.fase === "lobby";
  const ultima = v.pregunta?.numero === v.totalPreguntas;

  return (
    <div className="reto">
      <FondoDeReto />
      <ReaccionesFlotantes reacciones={v.reacciones} />
      <header className="relative z-10 flex flex-wrap items-center gap-3 px-6 py-4">
        <Logo variante="blanco" alto={32} />
        <div className="min-w-0 flex-1 border-l border-white/20 pl-3">
          <div className="truncate font-display text-[16px] font-bold">{v.titulo}</div>
          <div className="text-[12px] tracking-[.12em] text-sidebar-grupo uppercase">Reto SMG</div>
        </div>
        {v.pregunta && <PasosDelReto actual={v.pregunta.numero} total={v.totalPreguntas} />}
        {enEspera && v.modo === "en_vivo" && (
          <Boton
            variante="firma"
            disabled={ocupado || v.marcador.length === 0}
            onClick={() => iniciar.mutate({ id })}
          >
            🚀 Empezar
          </Boton>
        )}
        {v.fase === "resultado" && (
          <Boton variante="firma" disabled={ocupado} onClick={() => siguiente.mutate({ id })}>
            {ultima ? "🏆 Ver podio" : "Siguiente pregunta"}
          </Boton>
        )}
        {v.fase !== "terminada" && (
          <Boton variante="secundario" disabled={ocupado} onClick={() => terminar.mutate({ id })}>
            Terminar
          </Boton>
        )}
      </header>
      {error && <Aviso className="relative z-10 mx-6 mb-2">{error}</Aviso>}
      <main className="relative z-10 flex flex-1 flex-col justify-center px-6 pb-8">
        {enEspera && <SalaDeEspera vista={v} />}
        {(v.fase === "pregunta" || v.fase === "resultado") && <PreguntaEnPantalla vista={v} />}
        {v.fase === "terminada" && <Podio vista={v} />}
      </main>
    </div>
  );
}

function SalaDeEspera({ vista: v }: { vista: VistaDePresentador }) {
  const enlace = `${window.location.origin}/jugar?codigo=${v.codigo}`;
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    let vigente = true;
    QRCode.toString(enlace, { type: "svg", margin: 1, width: 220 })
      .then((s) => vigente && setQr(s))
      .catch(() => vigente && setQr(null));
    return () => {
      vigente = false;
    };
  }, [enlace]);

  return (
    <div className="mx-auto w-full max-w-[920px] text-center">
      <div className="reto-entra flex flex-wrap items-center justify-center gap-10">
        {qr && (
          <img
            src={`data:image/svg+xml;utf8,${encodeURIComponent(qr)}`}
            alt="Código QR para entrar al reto"
            width={220}
            height={220}
            className="rounded-xl bg-white p-2.5"
          />
        )}
        <div>
          <div className="text-[17px] text-sidebar-texto">
            Abre <b className="text-white">{window.location.host}/jugar</b> y escribe
          </div>
          <div className="mt-1 font-display text-[clamp(56px,9vw,92px)] leading-none font-extrabold tracking-[.06em]">
            {v.codigo}
          </div>
        </div>
      </div>
      <div className="mt-9 font-display text-[20px] font-bold">
        {v.marcador.length === 0 ? (
          <span className="block space-y-3">
            <BarrasLatiendo />
            <span className="block">Esperando a la gente…</span>
          </span>
        ) : v.marcador.length === 1 ? (
          "1 persona lista 🎉"
        ) : (
          `${v.marcador.length} personas listas 🎉`
        )}
      </div>
      <ul className="mt-4 flex max-h-[34vh] flex-wrap justify-center gap-2 overflow-hidden">
        {v.marcador.map((j) => (
          <li key={j.id} className="reto-avatar reto-pop">
            <span className="text-[20px]">{j.emoji}</span>
            {j.nombre}
            {v.modo === "a_tu_ritmo" && (
              <span className="text-sidebar-grupo">
                {j.respondidas}/{v.totalPreguntas}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Cuántos lugares subió o bajó cada quien respecto de la pregunta anterior. */
function useCambiosDeLugar(v: VistaDePresentador, revelada: boolean) {
  const previos = useRef(new Map<string, number>());
  const [cambios, setCambios] = useState(new Map<string, number>());
  const numero = v.pregunta?.numero;
  // biome-ignore lint/correctness/useExhaustiveDependencies: se mide una vez por pregunta, no en cada consulta
  useEffect(() => {
    if (!revelada) return;
    setCambios(
      new Map(
        v.marcador.map((j) => [j.id, (previos.current.get(j.id) ?? j.posicion) - j.posicion]),
      ),
    );
    previos.current = new Map(v.marcador.map((j) => [j.id, j.posicion]));
  }, [revelada, numero]);
  return cambios;
}

function PreguntaEnPantalla({ vista: v }: { vista: VistaDePresentador }) {
  const revelada = v.fase === "resultado";
  const cambios = useCambiosDeLugar(v, revelada);
  if (!v.pregunta) return null;
  const mayor = Math.max(...v.conteos, 1);
  const avance = v.marcador.length ? (v.respondieron / v.marcador.length) * 100 : 0;

  return (
    // La clave repite la entrada en cada pregunta; va aquí adentro para no perder los lugares.
    <div key={v.pregunta.id} className="mx-auto w-full max-w-[1100px]">
      <h1 className="reto-entra text-center text-[clamp(26px,4.4vw,48px)] leading-tight font-extrabold">
        {v.pregunta.texto}
      </h1>
      <div className="mx-auto my-6 max-w-[720px]">
        {revelada ? (
          <p className="text-center text-[18px]">
            {v.respondieron} de {v.marcador.length} respondieron
          </p>
        ) : (
          <>
            <Reloj restanteMs={v.restanteMs} segundos={v.pregunta.segundos} clave={v.pregunta.id} />
            <div className="mt-3 flex items-center gap-3 text-[15px] text-sidebar-texto">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-acceso transition-[width] duration-500"
                  style={{ width: `${avance}%` }}
                />
              </div>
              {v.respondieron} de {v.marcador.length} ✋
            </div>
          </>
        )}
      </div>
      <ul className="grid gap-3 sm:grid-cols-2">
        {v.pregunta.opciones.map((texto, i) => {
          const buena = revelada && i === v.correcta;
          const votos = v.conteos[i] ?? 0;
          return (
            <li
              key={texto}
              className={`reto-opcion reto-entra text-[clamp(17px,2.2vw,24px)] ${
                buena ? "reto-opcion-buena" : revelada ? "reto-opcion-apagada" : ""
              }`}
              style={{ animationDelay: `${i * 70}ms` }}
            >
              {revelada && (
                <span
                  aria-hidden="true"
                  className="reto-votos"
                  style={{ width: `${(votos / mayor) * 100}%` }}
                />
              )}
              <LetraOpcion indice={i} />
              <span className="relative min-w-0 flex-1">{texto}</span>
              {revelada && (
                <span className="relative font-display font-bold">
                  {buena && "✅ "}
                  {votos}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {revelada && v.marcador.length > 0 && (
        <ol className="mx-auto mt-6 max-w-[560px] space-y-1.5 text-[17px]">
          {v.marcador.slice(0, 5).map((j, i) => {
            const cambio = cambios.get(j.id) ?? 0;
            return (
              <li
                key={j.id}
                className="reto-entra flex items-center gap-3 rounded-lg bg-white/10 px-4 py-1.5"
                style={{ animationDelay: `${300 + i * 80}ms` }}
              >
                <span className="w-5 font-display font-bold">{j.posicion}</span>
                <span className="text-[20px]">{j.emoji}</span>
                <span className="min-w-0 flex-1 truncate">{j.nombre}</span>
                {cambio > 0 && <span className="text-[14px] text-acceso">▲ {cambio}</span>}
                <span className="font-display font-bold tabular-nums">{j.puntos}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/** Como las tres barras del logo: el segundo, el primero y el tercero, en ese orden de izquierda. */
const LUGARES = [
  { posicion: 2, alto: 0.62, medalla: "🥈", retraso: "0.9s" },
  { posicion: 1, alto: 0.86, medalla: "🥇", retraso: "1.8s" },
  { posicion: 3, alto: 0.42, medalla: "🥉", retraso: "0s" },
];

function Podio({ vista: v }: { vista: VistaDePresentador }) {
  return (
    <div className="mx-auto w-full max-w-[820px] text-center">
      {v.marcador.length > 0 && <Confeti cantidad={70} />}
      <h1 className="reto-pop mb-6 text-[clamp(30px,5vw,48px)] font-extrabold">
        ¡Reto cumplido! 🎉
      </h1>
      {v.marcador.length === 0 ? (
        <p>Nadie llegó a jugar.</p>
      ) : (
        <div className="reto-podio">
          {LUGARES.map(({ posicion, alto, medalla, retraso }) => {
            const j = v.marcador.find((x) => x.posicion === posicion);
            if (!j) return null;
            return (
              <div
                key={posicion}
                className="reto-podio-lugar"
                style={{ animationDelay: retraso, "--reto-alto": alto } as React.CSSProperties}
              >
                <div className="reto-podio-datos">
                  <div className="text-[clamp(30px,5vw,46px)] leading-none">{j.emoji}</div>
                  <div className="truncate text-[clamp(15px,2vw,20px)] font-bold">{j.nombre}</div>
                  <div className="font-display text-[15px] text-sidebar-texto tabular-nums">
                    {medalla} {j.puntos}
                  </div>
                </div>
                <div
                  className={`reto-podio-barra ${posicion === 1 ? "reto-podio-barra-primera" : ""}`}
                  style={{ animationDelay: retraso }}
                />
              </div>
            );
          })}
        </div>
      )}
      <Link
        to="/partidas/$id"
        params={{ id: v.id }}
        className="mt-8 inline-block text-[15px] font-semibold text-sidebar-texto underline"
      >
        Ver todos los resultados
      </Link>
    </div>
  );
}
